/**
 * Empty State Component
 * Displays a friendly message when no data is available
 * Includes preset configurations for common scenarios
 */

import React from "react";
import { Box, Typography, Button, SvgIconProps } from "@mui/material";
import {
  Inbox as InboxIcon,
  Search as SearchIcon,
  Add as AddIcon,
  People as PeopleIcon,
  School as SchoolIcon,
  FamilyRestroom as FamilyIcon,
  Class as ClassIcon,
  Receipt as ReceiptIcon,
  Payment as PaymentIcon,
  Description as DocumentIcon,
  FilterList as FilterIcon,
  Error as ErrorIcon,
  CheckCircle as SuccessIcon,
} from "@mui/icons-material";

/**
 * Preset configurations for common empty states
 */
export const emptyStatePresets = {
  students: {
    icon: <SchoolIcon />,
    title: "No students yet",
    description: "Get started by enrolling your first student.",
    actionLabel: "Add Student",
  },
  families: {
    icon: <FamilyIcon />,
    title: "No families yet",
    description: "Create a family to start managing student enrollments.",
    actionLabel: "Create Family",
  },
  classes: {
    icon: <ClassIcon />,
    title: "No classes yet",
    description: "Create your first class to organize students.",
    actionLabel: "Create Class",
  },
  invoices: {
    icon: <ReceiptIcon />,
    title: "No invoices yet",
    description: "Invoices will appear here once generated.",
    actionLabel: "Generate Invoice",
  },
  payments: {
    icon: <PaymentIcon />,
    title: "No payments yet",
    description: "Payment records will appear here.",
    actionLabel: "Record Payment",
  },
  users: {
    icon: <PeopleIcon />,
    title: "No users yet",
    description: "Add users to grant access to the system.",
    actionLabel: "Add User",
  },
  documents: {
    icon: <DocumentIcon />,
    title: "No documents yet",
    description: "Upload documents to get started.",
    actionLabel: "Upload Document",
  },
  searchResults: {
    icon: <SearchIcon />,
    title: "No results found",
    description: "Try adjusting your search or filter criteria.",
    secondaryActionLabel: "Clear Filters",
  },
  filteredResults: {
    icon: <FilterIcon />,
    title: "No matching items",
    description: "No items match your current filters. Try adjusting or clearing them.",
    secondaryActionLabel: "Clear Filters",
  },
  error: {
    icon: <ErrorIcon />,
    title: "Something went wrong",
    description: "We couldn't load the data. Please try again.",
    actionLabel: "Retry",
  },
  success: {
    icon: <SuccessIcon />,
    title: "All done!",
    description: "There are no pending items to review.",
  },
} as const;

export type EmptyStatePreset = keyof typeof emptyStatePresets;

export interface EmptyStateProps {
  /** Use a preset configuration */
  preset?: EmptyStatePreset;
  /** Icon to display (defaults to InboxIcon) */
  icon?: React.ReactElement<SvgIconProps>;
  /** Main title text */
  title?: string;
  /** Description text below the title */
  description?: string;
  /** Primary action button text */
  actionLabel?: string;
  /** Primary action callback */
  onAction?: () => void;
  /** Secondary action button text */
  secondaryActionLabel?: string;
  /** Secondary action callback */
  onSecondaryAction?: () => void;
  /** Whether this is a "no search results" state */
  isSearchResult?: boolean;
  /** Minimum height for the container */
  minHeight?: number | string;
  /** Visual variant */
  variant?: "default" | "compact" | "inline";
}

const EmptyStateComponent: React.FC<EmptyStateProps> = ({
  preset,
  icon,
  title,
  description,
  actionLabel,
  onAction,
  secondaryActionLabel,
  onSecondaryAction,
  isSearchResult = false,
  minHeight = 300,
  variant = "default",
}) => {
  // Merge preset with explicit props (explicit props override preset)
  const presetConfig = preset ? emptyStatePresets[preset] : null;
  const resolvedIcon =
    icon ||
    presetConfig?.icon ||
    (isSearchResult ? <SearchIcon /> : <InboxIcon />);
  const resolvedTitle = title || presetConfig?.title || "No data";
  const resolvedDescription = description || presetConfig?.description;
  const resolvedActionLabel = actionLabel || (presetConfig && 'actionLabel' in presetConfig ? presetConfig.actionLabel : undefined);
  const resolvedSecondaryLabel =
    secondaryActionLabel || (presetConfig && 'secondaryActionLabel' in presetConfig ? presetConfig.secondaryActionLabel : undefined);

  // Variant-based styling
  const isCompact = variant === "compact";
  const isInline = variant === "inline";

  if (isInline) {
    return (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 2,
          py: 3,
          px: 2,
          color: "text.secondary",
        }}
      >
        {React.cloneElement(resolvedIcon, {
          sx: { fontSize: 24, color: "text.disabled" },
        })}
        <Box>
          <Typography variant="body2" sx={{
            fontWeight: 500
          }}>
            {resolvedTitle}
          </Typography>
          {resolvedDescription && (
            <Typography variant="caption" sx={{
              color: "text.secondary"
            }}>
              {resolvedDescription}
            </Typography>
          )}
        </Box>
        {resolvedActionLabel && onAction && (
          <Button size="small" onClick={onAction} sx={{ ml: "auto" }}>
            {resolvedActionLabel}
          </Button>
        )}
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        minHeight: isCompact ? 200 : minHeight,
        py: isCompact ? 4 : 6,
        px: 3,
        textAlign: "center",
      }}
    >
      <Box
        sx={{
          width: isCompact ? 60 : 80,
          height: isCompact ? 60 : 80,
          borderRadius: "50%",
          bgcolor: "action.hover",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          mb: isCompact ? 2 : 3,
        }}
      >
        {React.cloneElement(resolvedIcon, {
          sx: { fontSize: isCompact ? 28 : 40, color: "text.secondary" },
        })}
      </Box>
      <Typography
        variant={isCompact ? "subtitle1" : "h6"}
        gutterBottom
        sx={{
          color: "text.primary",
          fontWeight: 600
        }}>
        {resolvedTitle}
      </Typography>
      {resolvedDescription && (
        <Typography
          variant="body2"
          sx={{
            color: "text.secondary",
            maxWidth: 400,
            mb: resolvedActionLabel || resolvedSecondaryLabel ? 3 : 0
          }}>
          {resolvedDescription}
        </Typography>
      )}
      {(resolvedActionLabel || resolvedSecondaryLabel) && (
        <Box sx={{ display: "flex", gap: 2, mt: 2, flexWrap: "wrap", justifyContent: "center" }}>
          {resolvedSecondaryLabel && onSecondaryAction && (
            <Button
              variant="outlined"
              size={isCompact ? "small" : "medium"}
              onClick={onSecondaryAction}
            >
              {resolvedSecondaryLabel}
            </Button>
          )}
          {resolvedActionLabel && onAction && (
            <Button
              variant="contained"
              size={isCompact ? "small" : "medium"}
              startIcon={<AddIcon />}
              onClick={onAction}
            >
              {resolvedActionLabel}
            </Button>
          )}
        </Box>
      )}
    </Box>
  );
};

/**
 * Memoized EmptyState - prevents unnecessary re-renders
 */
export const EmptyState = React.memo(EmptyStateComponent);

export default EmptyState;
